import { Link } from 'react-router-dom'
import { History, Plus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { DashboardCommercialDiscussionClientV2Dto } from '@/lib/api/v2/dashboard'

interface CommercialDiscussionCardIssuedProps {
  item: DashboardCommercialDiscussionClientV2Dto
  onLogFollowUp: () => void
  onShowHistory: () => void
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('fr-BE', {
      day: '2-digit',
      month: '2-digit',
    }).format(d)
  } catch {
    return '—'
  }
}

/**
 * Card variant for the "Devis envoyé" filter (SOLY-SALES-001 mini-scope 03).
 * Adds 2 dates (envoyé · relancé), a "+ relance" button, and a "Historique (N)"
 * button when at least one follow-up has been logged.
 *
 * Card itself stays a Link to the client detail page (preserves existing nav).
 * Buttons stop propagation so clicking them doesn't navigate away.
 */
export function CommercialDiscussionCardIssued({
  item,
  onLogFollowUp,
  onShowHistory,
}: CommercialDiscussionCardIssuedProps) {
  const sentLabel = formatShortDate(item.issuedAt)
  const relancedLabel = formatShortDate(item.lastFollowUpAt)
  const followUpCount = item.followUpCount ?? 0

  const handleFollowUpClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onLogFollowUp()
  }

  const handleHistoryClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onShowHistory()
  }

  return (
    <Link
      to={`/clients/${item.customerId}`}
      className="block rounded-lg border bg-gradient-to-br from-muted/10 via-background to-background p-4 hover:bg-muted/20"
      data-testid="commercial-discussion-card-issued"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-semibold truncate">{item.name || '—'}</div>
        </div>
        {item.preferredLanguageLabel && (
          <div className="shrink-0">
            <Badge variant="outline" className="rounded-md">
              {item.preferredLanguageLabel}
            </Badge>
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        Envoyé {sentLabel}
        <span className="mx-1.5 text-muted-foreground/50">·</span>
        Relancé {relancedLabel}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          {item.salesOwner?.initials && (
            <Badge variant="outline" className="rounded-full px-2">
              {item.salesOwner.initials}
            </Badge>
          )}
          {item.salesOwner?.fullName && <span className="truncate">{item.salesOwner.fullName}</span>}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {followUpCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleHistoryClick}
              data-testid="follow-up-history-btn"
              className="gap-1.5"
              title="Voir l'historique des relances"
            >
              <History className="h-3.5 w-3.5" />
              Historique ({followUpCount})
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleFollowUpClick}
            data-testid="follow-up-add-btn"
            className="gap-1.5"
            title="Logger une relance"
          >
            <Plus className="h-3.5 w-3.5" />
            relance
          </Button>
        </div>
      </div>
    </Link>
  )
}
